import * as docx from 'docx';

import { DueDiligencePageGetDueDiligenceRecordQuery, DueDiligencePageGetLatestDueDiligenceFunnelQuery } from '@/__generated__/graphql';
import client from '@/apollo-client';
import { InfoIcon } from '@/assets/icons/info-icons/info';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import CheckBox from '@/components/basicComponents/checkbox';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import RadioButton from '@/components/basicComponents/radioButton';
import Select from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { Loader } from '@/components/loaders/loader';
import { getSettingsItemValue } from '@/components/settings-page';
import { FirmInfoType } from '@/components/settings-page/platformSettings/firmInfo';
import { ITemplateStylingSettings } from '@/components/settings-page/platformSettings/templatesSettings/types';
import { SYSTEM_SETTINGS_QUERY, TENANT_NAME_QUERY } from '@/components/settings-page/queries';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { IFieldEditor } from '@/shared/types/fieldEditor';
import { convertDueDiligenceFieldToFieldEditor } from '@/shared/ui/fieldList';
import { formattingDueDiligenceRecordDataForDocx } from '@/shared/ui/templates/docx-templates/dueDiligenceRecord-DOCXTemplate';
import { convertMarkdownToDocx, generateDocxTemplate } from '@/shared/ui/templates/docx-templates/utils';
import { DueDiligenceRecordPDFTemplate } from '@/shared/ui/templates/pdf-templates/DueDiligenceRecord-PDFTemplate';
import { formatAiFieldValueForTemplate, formatFieldValueForTemplate } from '@/shared/ui/templates/utils';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { safeJsonParse } from '@/shared/utils/json';
import { themeVar } from '@/styles/theme';
import { useQuery } from '@apollo/client';
import { AIFieldType, PromptGroupOutputStructureSchemaType } from '@trace-8/trace8-schema';
import axios from 'axios';
import { IPatch, patchDocument, PatchType } from 'docx';
import { AnimatePresence, motion } from 'framer-motion';
import fileDownload from 'js-file-download';
import { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { DUE_DILIGENCE_RECORD_QUERY, EXPORT_DUE_DILIGENCE_CSV, GET_LATEST_DUE_DILIGENCE_FUNNEL_QUERY } from '../queries';

enum DownloadFormatEnum {
  PDF = 'PDF',
  DOCX = 'DOCX',
  CSV = 'CSV'
}

enum TemplateTypeRadioButtonOptionEnum {
  CustomExportTemplate = 'Custom Export Template',
  DefaultExportTemplate = 'Default Export Template'
}

const downloadFormatOptions = Object.values(DownloadFormatEnum).map((format) => createSimpleSelectOption(format));
const templateTypeRadioButtonOptions = Object.values(TemplateTypeRadioButtonOptionEnum).map((option) => option);

interface DownloadPDFProps {
  isOpen: boolean;
  activeTab?: string;
  recordId: string | null;
  onClose: () => void;
}

const DownloadPDF = ({ isOpen, activeTab, recordId, onClose }: DownloadPDFProps) => {
  const { user } = useAuth();
  const theme = useTheme();
  const { default: defaultErrorMessage } = useErrorMessages();

  const [selectedFormat, setSelectedFormat] = useState<DownloadFormatEnum | null>(null);
  const [templateType, setTemplateType] = useState<TemplateTypeRadioButtonOptionEnum | null>(TemplateTypeRadioButtonOptionEnum.CustomExportTemplate);
  const [selectedTierId, setSelectedTierId] = useState('');
  const [includeAiSuggested, setIncludeAiSuggested] = useState(true);
  const [isDocumentGenerating, setIsDocumentGenerating] = useState(false);
  const [isTiersLoading, setIsTiersLoading] = useState(false);
  const [tiers, setTiers] = useState<DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers']>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [firmInfoData, setFirmInfoData] = useState<FirmInfoType>({} as FirmInfoType);
  const [templateStylingData, setTemplateStylingData] = useState<ITemplateStylingSettings>({});
  const [docxTemplateStorageKey, setDocxTemplateStorageKey] = useState<string | null>(null);

  const { data: dueDiligenceRecordData, loading: dueDiligenceRecordLoading } = useQuery(DUE_DILIGENCE_RECORD_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      id: recordId
    },
    skip: !recordId,
    onCompleted: async (data) => {
      setIsTiersLoading(true);
      const { id: funnelId, tiers, docxTemplateStorageKey } = data?.getDueDiligenceRecord?.funnel || {};

      const { data: funnelData } = await client.query({
        fetchPolicy: 'network-only',
        query: GET_LATEST_DUE_DILIGENCE_FUNNEL_QUERY,
        variables: {
          id: funnelId
        }
      });

      const updatedTiers: DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'] = tiers.map(
        (oldTier: DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]) => {
          const matchingTier = funnelData.getDueDiligenceFunnel.latestFunnel.tiers.find(
            (tier: DueDiligencePageGetLatestDueDiligenceFunnelQuery['getDueDiligenceFunnel']['latestFunnel']['tiers'][number]) =>
              tier.order === oldTier.order
          );

          if (matchingTier) {
            return {
              ...oldTier,
              name: matchingTier.name
            };
          }
          return oldTier;
        }
      );

      setTiers(updatedTiers);
      setDocxTemplateStorageKey(docxTemplateStorageKey);

      if (updatedTiers?.length === 1) {
        setSelectedTierId(updatedTiers?.[0]?.id || '');
      }

      if (activeTab) {
        const selectedTier = updatedTiers?.find((tier) => tier.name === activeTab);
        setSelectedTierId(selectedTier?.id || '');
      }

      setIsTiersLoading(false);
    }
  });

  const { data: tenantNameData, loading: tenantNameLoading } = useQuery(TENANT_NAME_QUERY, {
    variables: {
      id: user?.tenant?.id
    }
  });

  const { data: firmInfoQueryData, loading: firmInfoLoading } = useQuery(SYSTEM_SETTINGS_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      data: {
        key: 'firmInfo',
        type: 'config',
        allowNullTenantSettings: true
      }
    }
  });

  useEffect(() => {
    if (tenantNameLoading || !tenantNameData || firmInfoLoading || !firmInfoQueryData) return;

    const firmInfo = getSettingsItemValue(firmInfoQueryData?.systemSettings ?? [], 'firmInfo') || {};
    setFirmInfoData({ ...firmInfo, firmName: tenantNameData?.tenant?.name || '' });
  }, [tenantNameLoading, tenantNameData, firmInfoLoading, firmInfoQueryData]);

  const { loading: templateStylingLoading } = useQuery(SYSTEM_SETTINGS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      data: {
        tenantId: user?.tenant?.id,
        type: 'customization'
      }
    },
    onCompleted: (data) => {
      const templateStyling = getSettingsItemValue(data?.systemSettings ?? [], 'templateStyling') || {};
      setTemplateStylingData(templateStyling);
    }
  });

  const createTextRunsWithLineBreaks = (text: string, bold: boolean) => {
    const formattedText = text?.split('\n').flatMap((line, index, array) => {
      return index < array.length - 1
        ? [
            new docx.TextRun({
              text: line,
              bold
            }),
            new docx.TextRun({ break: 1 })
          ]
        : new docx.TextRun({
            text: line,
            bold
          });
    });

    return formattedText;
  };

  const getCitationPageLink = (filename: string, pageNumber: string, fieldEditor: IFieldEditor) => {
    const baseUrl = location.origin + location.pathname;
    const params = new URLSearchParams({
      fieldId: fieldEditor.id,
      editFieldPageType: fieldEditor.documentFieldType,
      isFromDocx: 'true',
      filename: encodeURIComponent(filename),
      pageNumber
    });

    return `${baseUrl}?${params.toString()}`;
  };

  const getCitationPageLinks = (filename: string, pageNumbers: string[], fieldEditor: IFieldEditor) => {
    return pageNumbers.map((pageNumber, index) => {
      return new docx.ExternalHyperlink({
        children: [new docx.TextRun({ text: `${pageNumber}${index < pageNumbers.length - 1 ? ', ' : ''}`, style: 'Hyperlink' })],
        link: getCitationPageLink(filename, pageNumber, fieldEditor)
      });
    });
  };

  const getCitationCommentItem = (answer: string, filename: string, pageNumbers: string[], fieldEditor: IFieldEditor) => {
    return [new docx.TextRun({ text: `${filename}: ` }), ...getCitationPageLinks(filename, pageNumbers, fieldEditor), new docx.TextRun({ break: 2 })];
  };

  const getCitationsComment = (fieldEditor: IFieldEditor) => {
    return [
      new docx.TextRun({ text: 'AI Answer:', bold: true }),
      new docx.TextRun({ break: 1 }),
      new docx.TextRun({ text: fieldEditor?.AI?.citations?.[0]?.answer || '' }),
      new docx.TextRun({ break: 2 }),
      new docx.TextRun({ text: 'Page References: ', bold: true }),
      new docx.TextRun({ break: 1 }),
      ...fieldEditor?.AI?.citations?.flatMap((citation) => {
        if (citation?.fileReferences?.length > 0) {
          return citation?.fileReferences?.flatMap((file: any) =>
            getCitationCommentItem(citation?.answer || '', file?.filename || '', file?.pageNumbers || [], fieldEditor)
          );
        } else {
          return getCitationCommentItem(citation?.answer || '', citation?.filename || '', citation?.pageNumbers || [], fieldEditor);
        }
      })
    ];
  };

  const generateDocxDocumentPatches = async (fields: IFieldEditor[], headerFontColor: string, accentColor: string) => {
    const documentPatches: Record<string, IPatch> = {};
    let commentIdCounter = 9999;

    for (const fieldEditor of fields) {
      const commentId = commentIdCounter++;
      const { value: fieldValue, commentary: fieldCommentary, attachments: fieldAttachments } = fieldEditor;
      const shouldUseAISuggestion = fieldValue === null && fieldCommentary === null && fieldEditor.AI?.answers?.length && includeAiSuggested;
      const hasAttachments = fieldAttachments?.length;

      if (fieldEditor.docxKey) {
        if (fieldValue === null && fieldCommentary === null && !hasAttachments && !fieldEditor.AI?.answers?.length) {
          documentPatches[fieldEditor.docxKey] = {
            type: PatchType.PARAGRAPH,
            children: [new docx.TextRun('')]
          };
        }

        if (fieldValue || fieldCommentary || hasAttachments) {
          if (fieldEditor.documentFieldType === getEnumKeyByValue(AIFieldType, AIFieldType.JSON)) {
            generateDocxDocumentPatchesForJson(fieldEditor, fieldValue, documentPatches);
          } else if (fieldEditor.documentFieldType === getEnumKeyByValue(AIFieldType, AIFieldType.LONG_FORM_CONTENT)) {
            const markdownContent = fieldValue || '';
            const renderedMarkdownElements = await convertMarkdownToDocx({
              markdown: markdownContent,
              attachments: fieldAttachments,
              theme,
              headerFontColor,
              accentColor
            });
            documentPatches[fieldEditor.docxKey] = {
              type: PatchType.DOCUMENT,
              children: renderedMarkdownElements.filter(Boolean)
            };
          } else {
            const formattedTextRuns = createTextRunsWithLineBreaks(
              formatFieldValueForTemplate(fieldEditor.documentFieldType, fieldValue, fieldCommentary),
              false
            );
            if (formattedTextRuns) {
              documentPatches[fieldEditor.docxKey] = {
                type: PatchType.PARAGRAPH,
                children: formattedTextRuns
              };
            }
          }
        }

        if (shouldUseAISuggestion) {
          if (fieldEditor.documentFieldType === getEnumKeyByValue(AIFieldType, AIFieldType.JSON)) {
            generateDocxDocumentPatchesForJson(fieldEditor, fieldEditor.AI.answerJson, documentPatches);
          } else if (fieldEditor.documentFieldType === getEnumKeyByValue(AIFieldType, AIFieldType.LONG_FORM_CONTENT)) {
            const aiMarkdownContent = fieldEditor.AI.answers[0] || '';
            const renderedAIMarkdownElements = await convertMarkdownToDocx({
              markdown: aiMarkdownContent,
              attachments: fieldAttachments,
              theme,
              headerFontColor,
              accentColor
            });
            documentPatches[fieldEditor.docxKey] = {
              type: PatchType.DOCUMENT,
              children: [
                ...(fieldEditor.AI.citations.filter((citation) => !!citation?.answer).length > 0
                  ? [
                      new docx.CommentRangeStart(commentId),
                      ...renderedAIMarkdownElements.filter(Boolean),
                      new docx.CommentRangeEnd(commentId),
                      new docx.Paragraph({
                        children: [
                          new docx.Comment({
                            id: commentId,
                            author: 'AI Citations',
                            children: [
                              new docx.Paragraph({
                                children: getCitationsComment(fieldEditor)
                              })
                            ]
                          })
                        ]
                      })
                    ]
                  : [...renderedAIMarkdownElements.filter(Boolean)])
              ]
            };
          } else {
            const aiFormattedTextRuns = createTextRunsWithLineBreaks(
              formatAiFieldValueForTemplate(fieldEditor.documentFieldType, fieldEditor.AI.answers[0]),
              false
            );
            if (aiFormattedTextRuns) {
              documentPatches[fieldEditor.docxKey] = {
                type: PatchType.PARAGRAPH,
                children: [
                  ...(fieldEditor.AI.citations.filter((citation) => !!citation?.answer).length > 0
                    ? [
                        new docx.CommentRangeStart(commentId),
                        ...aiFormattedTextRuns,
                        new docx.CommentRangeEnd(commentId),
                        new docx.Comment({
                          id: commentId,
                          author: 'AI Citations',
                          children: [
                            new docx.Paragraph({
                              children: getCitationsComment(fieldEditor)
                            })
                          ]
                        })
                      ]
                    : [...aiFormattedTextRuns])
                ]
              };
            }
          }
        }
      }
    }

    return documentPatches;
  };

  const generateDocxDocumentPatchesForJson = (fieldEditor: IFieldEditor, answerString: string, documentPatches: Record<string, IPatch>) => {
    const answerAsJson = answerString ? JSON.parse(answerString) : null;
    if (answerAsJson) {
      if (Array.isArray(answerAsJson)) {
        answerAsJson.forEach((item, index) => {
          if (typeof item === 'object' && item !== null) {
            // Handles array of objects. patch per property of each object
            Object.entries(item).forEach(([key, value]) => {
              const docxKey = `${fieldEditor.docxKey}_${index}_${key}`;
              const textRuns = createTextRunsWithLineBreaks(value as string, false);

              if (textRuns) {
                documentPatches[docxKey] = {
                  type: PatchType.PARAGRAPH,
                  children: textRuns
                };
              }
            });
          } else {
            // Handle simple array. patch per item
            const docxKey = `${fieldEditor.docxKey}_${index}`;
            const textRuns = createTextRunsWithLineBreaks(item as string, false);

            if (textRuns) {
              documentPatches[docxKey] = {
                type: PatchType.PARAGRAPH,
                children: textRuns
              };
            }
          }
        });
      } else if (typeof answerAsJson === 'object' && answerAsJson !== null) {
        // Handle object. patch per property
        Object.entries(answerAsJson).forEach(([key, value]) => {
          const docxKey = `${fieldEditor.docxKey}_${key}`;
          const textRuns = createTextRunsWithLineBreaks(value as string, false);

          if (textRuns) {
            documentPatches[docxKey] = {
              type: PatchType.PARAGRAPH,
              children: textRuns
            };
          }
        });
      }
    }
  };

  const getAllPossibleDocumentPatches = (allTierFields: IFieldEditor[]) => {
    const allPossibleDocumentPatches: Record<string, IPatch> = {};

    for (const field of allTierFields) {
      if (field.documentFieldType === getEnumKeyByValue(AIFieldType, AIFieldType.JSON)) {
        const { jsonFormat } = safeJsonParse(field?.promptGroup?.outputStructure) || {};
        if (jsonFormat?.type === PromptGroupOutputStructureSchemaType.OBJECT) {
          Object.entries(jsonFormat?.properties || {}).forEach(([key]) => {
            allPossibleDocumentPatches[`${field.docxKey}_${key}`] = {
              type: PatchType.PARAGRAPH,
              children: [new docx.TextRun('')]
            };
          });
        }
        if (jsonFormat?.type === PromptGroupOutputStructureSchemaType.ARRAY) {
          if (jsonFormat?.items?.type === PromptGroupOutputStructureSchemaType.OBJECT) {
            const array = Array(20).fill(jsonFormat?.items?.properties);
            array.forEach((item, index) => {
              Object.entries(item).forEach(([key]) => {
                allPossibleDocumentPatches[`${field.docxKey}_${index}_${key}`] = {
                  type: PatchType.PARAGRAPH,
                  children: [new docx.TextRun('')]
                };
              });
            });
          } else {
            const array = Array(20).fill('');
            array.forEach((item, index) => {
              allPossibleDocumentPatches[`${field.docxKey}_${index}`] = {
                type: PatchType.PARAGRAPH,
                children: [new docx.TextRun('')]
              };
            });
          }
        }
      } else {
        allPossibleDocumentPatches[field.docxKey] = {
          type: PatchType.PARAGRAPH,
          children: [new docx.TextRun('')]
        };
      }
    }

    return allPossibleDocumentPatches;
  };

  const handleGenerateCustomDocx = async () => {
    try {
      setIsDocumentGenerating(true);
      setErrorMessage(null);

      const { funnel, investment } = dueDiligenceRecordData?.getDueDiligenceRecord || {};

      if (!funnel?.docxTemplateStorageKey) {
        throw new Error('Docx template storage key is not found.');
      }

      const selectedTier = tiers.find((tier) => tier.id === selectedTierId);

      if (!selectedTier) {
        throw new Error('Cannot find selected tier.');
      }

      const headerFontColor = templateStylingData?.headerFontStyle?.fontColor
        ? `#${templateStylingData?.headerFontStyle?.fontColor}`
        : theme.font.strong;
      const accentColor = templateStylingData?.accentColor || '#5A6771';
      const allTierFields = selectedTier.sections.flatMap((section) => section.fields);
      const convertedFieldEditors = convertDueDiligenceFieldToFieldEditor(allTierFields || [], null);
      const allPossibleDocumentPatches = getAllPossibleDocumentPatches(convertedFieldEditors);
      const documentPatches = await generateDocxDocumentPatches(convertedFieldEditors, headerFontColor, accentColor);

      const { data: templateArrayBuffer } = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/due-diligence-funnel/${funnel?.id}/download-docx-template`,
        {
          headers: { authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'arraybuffer'
        }
      );

      const patchedDocument = await patchDocument({
        outputType: 'nodebuffer',
        data: templateArrayBuffer,
        recursive: true,
        keepOriginalStyles: true,
        patches: { ...allPossibleDocumentPatches, ...documentPatches }
      });

      const fileName = `${investment?.name || 'document'}.docx`;
      fileDownload(patchedDocument, fileName);
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setIsDocumentGenerating(false);
    }
  };

  const handleGenerateDocx = async () => {
    try {
      setIsDocumentGenerating(true);
      setErrorMessage(null);

      const investmentName = dueDiligenceRecordData?.getDueDiligenceRecord?.investment?.name ?? '';
      const investmentDescription = dueDiligenceRecordData?.getDueDiligenceRecord?.investment?.description ?? '';
      const selectedTier = tiers.find((tier) => tier.id === selectedTierId);

      if (!selectedTier) return;

      const formattedData = await formattingDueDiligenceRecordDataForDocx(
        selectedTier,
        investmentName,
        investmentDescription,
        templateStylingData,
        includeAiSuggested,
        theme
      );
      const fileName = `${investmentName || 'document'}.docx`;

      await generateDocxTemplate(formattedData, firmInfoData, templateStylingData, fileName, theme);
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setIsDocumentGenerating(false);
    }
  };

  const handleGeneratePdf = async () => {
    try {
      setIsDocumentGenerating(true);
      setErrorMessage(null);

      const selectedTier = tiers.find((tier) => tier.id === selectedTierId);
      if (!selectedTier) {
        throw new Error('Cannot find selected tier.');
      }

      const { pdf } = await import('@react-pdf/renderer');
      const pdfBlob = await pdf(
        <DueDiligenceRecordPDFTemplate
          dueDiligenceRecordTier={selectedTier}
          investmentName={dueDiligenceRecordData?.getDueDiligenceRecord?.investment?.name}
          investmentDescription={dueDiligenceRecordData?.getDueDiligenceRecord?.investment?.description}
          firmInfoData={firmInfoData}
          templateStylingData={templateStylingData}
          includeAiSuggested={includeAiSuggested}
          theme={theme}
        />
      ).toBlob();

      const url = URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setIsDocumentGenerating(false);
    }
  };

  const handleGenerateCsv = async () => {
    try {
      setIsDocumentGenerating(true);
      setErrorMessage(null);

      const { data } = await client.query({
        fetchPolicy: 'network-only',
        query: EXPORT_DUE_DILIGENCE_CSV,
        variables: {
          id: recordId
        }
      });

      const blob = new Blob([data?.exportDueDiligenceCSV], { type: 'text/csv' });
      fileDownload(blob, `${recordId}.csv`);
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setIsDocumentGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedFormat && templateType === TemplateTypeRadioButtonOptionEnum.CustomExportTemplate) {
      await handleGenerateCustomDocx();
      onClose();
      return;
    }

    const downloadHandlers = {
      [DownloadFormatEnum.PDF]: handleGeneratePdf,
      [DownloadFormatEnum.DOCX]: handleGenerateDocx,
      [DownloadFormatEnum.CSV]: handleGenerateCsv
    };

    const handler = downloadHandlers[selectedFormat];
    if (handler) {
      await handler();
      onClose();
    }
  };

  const changeCheckboxValue = () => {
    setIncludeAiSuggested((prev) => !prev);
  };

  return (
    <ModalWrappedContent title="Download" isOpen={isOpen} onClose={onClose} showCancelButton={false}>
      {tenantNameLoading || firmInfoLoading || templateStylingLoading || dueDiligenceRecordLoading || isTiersLoading ? (
        <Loader />
      ) : (
        <>
          {errorMessage && (
            <ErrorBannerWrapper>
              <Banner icon={<InfoIcon width={26} height={26} />} title="Error" description={errorMessage} bgColor={themeVar('context.error')} />
            </ErrorBannerWrapper>
          )}
          <Container>
            {tiers?.length > 1 && (
              <Select
                label="Select Tier"
                value={selectedTierId}
                setValue={setSelectedTierId}
                options={tiers.map((tier) => ({ label: tier.name, value: tier.id }))}
                isRequiredField
              />
            )}
            {!!docxTemplateStorageKey && (
              <div>
                {templateTypeRadioButtonOptions.map((option) => (
                  <RadioButton
                    key={option}
                    name="templateType"
                    radioButtonText={option}
                    selectedOption={templateType}
                    handleRadioChange={() => setTemplateType(option)}
                  />
                ))}
              </div>
            )}
            <AnimatePresence>
              {(!docxTemplateStorageKey ||
                (!!docxTemplateStorageKey && templateType === TemplateTypeRadioButtonOptionEnum.DefaultExportTemplate)) && (
                <motion.div key="downloadFormatSelect" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <Select
                    label="Select Format"
                    value={selectedFormat}
                    setValue={(format) => setSelectedFormat(format as DownloadFormatEnum)}
                    options={downloadFormatOptions}
                    isRequiredField
                  />
                </motion.div>
              )}
            </AnimatePresence>
            <CheckboxWrapper id="input" onClick={changeCheckboxValue}>
              <CheckBox id="input" isActive={includeAiSuggested} /> Include AI Suggested Content
            </CheckboxWrapper>
          </Container>
          <DownloadButtonWrappper>
            <Button
              onClick={handleDownload}
              styleType={
                (!!docxTemplateStorageKey && templateType === TemplateTypeRadioButtonOptionEnum.DefaultExportTemplate && !selectedFormat) ||
                (!docxTemplateStorageKey && !selectedFormat) ||
                !selectedTierId ||
                isDocumentGenerating
                  ? 'disabled'
                  : 'default'
              }
              isLoading={isDocumentGenerating}
              size="md"
            >
              Download
            </Button>
          </DownloadButtonWrappper>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default DownloadPDF;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 15px;
`;

const CheckboxWrapper = styled.div`
  cursor: pointer;
  display: flex;
  gap: 9px;
  margin-top: 20px;
  padding: 18px 0 30px 0;
  border-top: 1px solid ${themeVar('border.base')};
  color: ${themeVar('font.base')};
  font-size: 16px;
  font-weight: 400;
`;

const DownloadButtonWrappper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 20px;
`;
